import {
  Address,
  AddressInput,
  CartState,
  Category,
  Coupon,
  CreateOrderData,
  DeliveryInterval,
  OrderReviewType,
  PaymentMethodIdEnum,
  Product,
  ProductImageTypes,
  ProductReview,
  ProductType,
  ShippingMethod,
  ShippingMethodDeliveryType,
  ShippingMethodType,
  ShippingZone,
  TOrder,
  User,
  RawCartResponse,
  PaymentMethod,
  HomeDataSection,
  Recipe,
  SelectionsSection,
  RecipesSection,
  SelectionsPayloadSection,
  PromoPayloadSection,
  ProductShelvesPayloadSection,
  CategoriesSection,
  CategoriesPayloadSection,
  RecipesPayloadSection,
  Promo,
  OrderHistory,
  Parhato2ProductSubscription,
  ProductSubscription,
  SupaAdditionalFeature,
  AdditionalFeature,
  CreateYooKassaPaymentInput,
  CreateYooKassaPaymentEF,
  ImageType,
  Notification,
  OrderTracking,
  ReferralStatsResponse,
  ReferralStats,
  Payments,
  OrderChat,
  OrderChatAttachment,
  OrderChatMessage,
  OrderChatMessagesPage,
  OrderChatUnreadSummary,
  OrderChatUnreadSummaryItem,
  CustomerWishlist,
} from "@core/types";
import {
  AddressSuggestionEF,
  CreateParhato2Order,
  Customer,
  Parhato2Coupon,
  Parhato2OrderWithItems,
  Parhato2PaymentMethodType,
  Parhato2ProductReview as productReview,
  ShippingZonesSupaNoCode,
} from "./types";
import { ShippingMethodMethodType, UpdateCustomerAddressMutationVariables } from "../../../../generated/graphql";
import { getPlatforms } from "@ionic/react";
import { useMarketExpressDataState } from "../../../../providers/marketMode";
import { getCustomerFullAddress } from "../../../../utils";
import {
  SupaShippingMethods,
  SupaProducts,
  SupaOrderReviews,
  SupaCategory,
  SupaPromo,
  SupaOrdersHistory,
  SupaRecipe,
  SupaNotifications,
  SupaOrderTracking,
  SupaPayments,
  SupaWishlist,
} from "../types/supabaseFragments";
import { removeHtmlEntities, supaImage } from "./utils";
import { Tables, TablesInsert } from "../types/supabase";
import { changeHost } from "../../../../utils/changeHost";

const codeBaseVersion = process.env.REACT_APP_PARHATO_CODE_VERSION!;

/**
 * @param order take a parhato order and convert to base order
 */
export function convertParhato2OrderToBaseOrder(order: Parhato2OrderWithItems): TOrder {
  const deliveryTime =
    order.delivery_interval_id.time_start && order.delivery_interval_id.time_end
      ? `${order.delivery_interval_id.time_start?.slice(0, 5)} - ${order.delivery_interval_id.time_end?.slice(0, 5)}`
      : "";

  const orderPayments = order.payments;

  const payments: Payments | undefined = orderPayments
    ? {
        id: orderPayments.id,
        amount: orderPayments.amount,
        status: orderPayments.status,
        comment: orderPayments.comment,
        orderId: orderPayments.order_id,
        updatedAt: orderPayments.updated_at,
        paymentId: orderPayments.payment_id,
        createdAt: orderPayments.created_at,
        customerId: orderPayments.customer_id,
        paymentMethod: orderPayments.payment_method,
        confirmationUrl: orderPayments.confirmation_url ?? "",
      }
    : undefined;

  return {
    //TODO IslamIB fix this
    coupons: [],
    payments,
    id: order.id,
    deliveryTime,
    total: order.total,
    number: String(order.id),
    needsPayment: order.needs_payment,
    status: order.status || "",
    dateCreated: order.created_at!,
    currencySymbol: order.currency === "643" ? "₽" : order.currency,
    customerNote: order.comment || "",
    rewardPoints: order.reward_points || 0,
    statusName: order.status_name || "",
    isExpressDelivery: order.is_express_delivery || false,
    paymentStatus: order.payment_status ?? "",
    pickerListPrinted: order.picker_list_printed || false,
    // @ts-ignore
    deliveryDate: order.delivery_date as Date,
    discountTotal: String(order.discount_total || "0"),
    shippingTotal: String(order.shipping_total || "0"),
    lineItems: order.line_items?.map((lineItem) => {
      const product = lineItem.product_id;
      const image = product?.images?.[0];
      return {
        id: product?.id,
        productId: product?.id,
        name: lineItem.name ?? "",
        quantity: lineItem.quantity ?? 0,
        price: String(lineItem.price ?? 0),
        total: String(lineItem.total ?? 0),
        variationId: product?.variations?.[0] ?? "",
        portion_unit: product?.portion_unit ?? "",
        attributes: product?.attributes?.map((attr) => ({
          id: String(attr?.id ?? ""),
          name: attr?.name ?? "",
          option: attr?.option ?? "",
          options: attr?.options ?? [],
        })),
        image: {
          src: supaImage(image?.src, "images"),
          id: image?.id,
          type: image?.base64 ? ProductImageTypes.base64 : ProductImageTypes.img,
          base64: image?.base64,
        },
      };
    }),
    shippingLine: {
      shipId: String(order.shipping_method_id),
      total: String(order.shipping_total),
      methodTitle: order.shipping_method_id.title ?? "",
      methodId: String(order.shipping_method_id.id),
    },
    paymentMethod: {
      id: String(order.payment_method_id.id),
      title: order.payment_method_id.title,
      type: order.payment_method_id.type === "bacs" ? PaymentMethodIdEnum.card : PaymentMethodIdEnum.cash,
    },
    customer: {
      id: String(order.customer.id),
      // @ts-ignore
      email: order.customer.email || "",
      phone: order.customer.phone,
      lastName: order.customer.lastName,
      firstName: order.customer.firstName,
      whatsappNumber: order.customer.whatsappNumber,
    },
    address: {
      id: order.address?.id || "",
      city: order.address?.city || "",
      house: order.address?.house || "",
      street: order.address?.street || "",
      latitude: order.address?.latitude || "",
      longitude: order.address?.longitude || "",
      intercom: order.address?.intercom || "",
      isFlat: order.address?.isFlat || false,
      floor: order.address?.floor || "",
      entrance: order.address?.entrance || "",
      apartmentNumber: order.address?.apartmentNumber || "",
    },
  };
}

export function convertParhato2OrderHistoryToBaseOrder(order: SupaOrdersHistory): OrderHistory {
  return {
    id: String(order.order_id),
    customerId: String(order.customer_id),
    deliveryDate: order.delivery_date ?? "",
    paymentStatus: order.payment_status ?? "",
    status: order.status ?? "",
    total: String(order.total),
    createdAt: order.created_at ?? "",
    statusName: order.status_name ?? "",
    shippingTotal: order.shipping_total ?? 0,
    isExpressDelivery: order.is_express_delivery ?? false,
    rewardPoints: order.reward_points ?? 0,
    itemsCount: order.products.length,
    reviewRating: order.review_rating ?? undefined,
    productImage: order.products.map((product) => ({
      productId: product.id,
      images: product.base64
        ? [
            {
              id: product.id,
              type: ProductImageTypes.base64,
              base64: product.base64,
              src: "",
              previewSrc: "",
            },
          ]
        : [],
    })),
  };
}

/**
 * Change the created order to the parhato type
 * @param order take a base order without id and convert to parhato order
 */
export function convertCreateOrderToParhato2(order: CreateOrderData): Partial<CreateParhato2Order> {
  const metaData = [
    {
      key: "weight",
      value: order.weight,
    },
    {
      key: "_order_whatsapp",
      value: order.customer?.whatsappNumber,
    },
    {
      key: "app_info",
      value: `from parhato app v${codeBaseVersion}; ${getPlatforms()}`,
    },
    {
      key: "kv",
      value: order.address?.isFlat ? "Квартира" : "Частный дом",
    },
    {
      key: "domofon",
      value: order.address?.intercom || "",
    },
    {
      key: "billing_address_3",
      value: order.address?.apartmentNumber || "",
    },
    {
      key: "billing_address_4",
      value: order?.address?.entrance || "",
    },
    {
      key: "search_address",
      value: order.address?.fullAddress || "",
    },
    {
      key: "billing_address_5",
      value: order.address?.floor || "",
    },
    {
      key: "longitude",
      value: order.address?.longitude || "",
    },
    {
      key: "latitude",
      value: order.address?.latitude || "",
    },
    {
      key: "address_id",
      value: order.address?.id || "",
    },
    {
      key: "entrance",
      value: order.address?.entrance || "",
    },
    {
      key: "floor",
      value: order.address?.floor || "",
    },
    {
      key: "apartment_number",
      value: order.address?.apartmentNumber || "",
    },
  ];

  if (order.deliveryDate) {
    metaData.push(
      {
        key: "pi_system_delivery_date",
        value:
          order.deliveryDate!.getFullYear() +
          "/" +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(order.deliveryDate!.getDate()).padStart(2, "0"),
      },
      {
        key: "pi_delivery_date",
        value:
          String(order.deliveryDate!.getDate()).padStart(2, "0") +
          "." +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "." +
          order.deliveryDate!.getFullYear(),
      },
      {
        key: "pi_delivery_type",
        value: "delivery",
      },
      {
        key: "pi_delivery_time",
        value: order.deliveryTime,
      },
    );
  }

  if (
    useMarketExpressDataState.getState().mode === "express" ||
    order.shippingLine.methodType === ShippingMethodType.taxiMaster
  ) {
    metaData.push({
      key: "parhato_delivery_type",
      value: "express",
    });
  }

  return {
    customer_id: Number(order.customer?.id!),

    line_items: order.lineItems
      ? order.lineItems.map((lineItem) => {
          return {
            name: lineItem.name,
            total: lineItem.total,
            price: lineItem.price,
            product_id: lineItem.id,
            quantity: lineItem.quantity,
            variation_id: lineItem.variationId ?? "",
            sku: lineItem.sku,
            portion_unit: lineItem.portion_unit,
          };
        })
      : [],

    status: order.status,
    customer_note: order.customerNote,
    shipping_lines: [
      {
        method_id: order.shippingLine.methodId!,
        method_title: order.shippingLine.methodTitle!,
        total: order.shippingLine.total ?? "0.00",
      },
    ],
    payment_method: order.paymentMethod.id,
    payment_method_title: order.paymentMethod.title,

    meta_data: metaData,

    billing: {
      notice: "",
      company: "",
      country: "",
      billing_country: "",

      phone: order?.customer?.phone ?? "",
      email: order?.customer?.email ?? "",

      last_name: order?.customer?.lastName ?? "",
      first_name: order?.customer?.firstName ?? "",

      billing_city: order?.address?.city ?? "",
      billing_address_2: order?.address?.house ?? "",
      billing_address_1: order?.address?.street ?? "",
      billing_address_5: order?.address?.floor ?? "",
      billing_address_4: order?.address?.entrance ?? "",
      billing_address_3: order?.address?.apartmentNumber ?? "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",
      kv: order?.address?.apartmentNumber ?? "",
      postcode: "",
      domofon: order?.address?.intercom ?? "",

      state: order?.address?.city ?? "",
      latitude: order?.address?.latitude ?? "",
      longitude: order?.address?.longitude ?? "",
      search_address: order?.address?.fullAddress ?? "",
    },
    shipping: {
      company: "",
      country: "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",

      shipping_phone: order?.customer?.whatsappNumber ?? "",
      first_name: order?.customer?.firstName ?? "",
      last_name: order?.customer?.lastName ?? "",
      phone: order?.customer?.phone ?? "",

      postcode: "",
      state: order?.address?.city ?? "",
    },
    coupon_lines: order.coupons?.map(({ code }) => ({ code })),
    reward_points: order.rewardPoints,
    cart_total: order.total,
    cash_given: order.cash_given,
  };
}

interface ITaxiShippingQuoteResponse {
  total: number | null;
  warnings?: Array<{
    code?: string;
    message: string;
    delay_min?: number;
    delay_max?: number;
  }>;
  quote_token?: string;
  quote_valid_for_seconds?: number;
  quote_expires_at?: string | null;
  conditions_version?: string;
}

export const convertTaxiShippingQuote = (
  quote: ITaxiShippingQuoteResponse | null | undefined,
): import("@core/types").TTaxiShippingQuote | null => {
  if (!quote) {
    return null;
  }

  return {
    total: quote.total,
    warnings:
      quote.warnings?.map((warning) => ({
        code: warning.code,
        message: warning.message,
        delayMin: warning.delay_min,
        delayMax: warning.delay_max,
      })) ?? [],
    quoteToken: quote.quote_token,
    quoteValidForSeconds: quote.quote_valid_for_seconds,
    quoteExpiresAt: quote.quote_expires_at,
    conditionsVersion: quote.conditions_version,
  };
};

export const convertAddressSuggestion = (address: AddressSuggestionEF) => {
  return {
    area: address.area,
    country: address.country,
    province: address.province,
    locality: address.locality,
    text: address.text,
    street: address.street,
    house: address.house,
    point: address.point,
    location: address.location,
    postcode: "",
    district: "",
  };
};

type NewShippingMethod = Omit<ShippingMethod, "maxDeliveryDays" | "minDeliveryDays" | "order">;

const shippingMethodDeliveryTypeMap: Record<ShippingMethodMethodType, ShippingMethodDeliveryType> = {
  [ShippingMethodMethodType.Default]: ShippingMethodDeliveryType.default,
  [ShippingMethodMethodType.NextDay]: ShippingMethodDeliveryType.nextDay,
};

export const convertShippingMethod = (shippingMethod: SupaShippingMethods) => {
  const toCamelCase = (str: string) =>
    str
      .toLowerCase()
      .split("_")
      .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join("");

  const camelCaseCode = toCamelCase(shippingMethod.code);
  const methodType = ShippingMethodType[camelCaseCode as keyof typeof ShippingMethodType];

  return {
    methodDeliveryType: shippingMethodDeliveryTypeMap.DEFAULT,
    methodType,
    id: String(shippingMethod.id),
    methodId: String(shippingMethod.id),
    description: shippingMethod.description,
    enabled: true,
    cost: shippingMethod.cost ? shippingMethod.cost : undefined,
    title: shippingMethod.title,
    minAmount: shippingMethod.min_amount ?? "",
  } as NewShippingMethod;
};

export const convertShippingZone = (shippingZone: ShippingZonesSupaNoCode): ShippingZone => {
  return {
    id: String(shippingZone.id),
    name: shippingZone.name,
  };
};

export function convertParhato2UserToUser(user: Customer): User {
  return {
    id: String(user.id),
    firstName: user.first_name,
    lastName: user.last_name,
    phone: user.billing.phone,
    email: user.email,
    userName: user.username,
    token: "",
  };
}

export function convertParhato2Coupon(coupon: Parhato2Coupon): Coupon {
  return {
    id: String(coupon.id),
    code: coupon.code,
    discountAmount: parseFloat(coupon.amount),
    discountType: coupon.discount_type,
    minimumAmount: parseFloat(coupon.minimum_amount),
    maximumAmount: parseFloat(coupon.maximum_amount),
    dateExpires: coupon.date_expires,
    individualUse: coupon.individual_use,
    freeShipping: coupon.free_shipping,
    excludeSaleItems: coupon.exclude_sale_items,
    productIds: coupon.product_ids,
    excludedProductIds: coupon.excluded_product_ids,
    productCategories: coupon.product_categories,
    excludedProductCategories: coupon.excluded_product_categories,
  };
}

const getProductImages = (productImages: SupaProducts["images"]): ImageType[] => {
  return productImages.map((img) => ({
    id: img?.id,
    src: supaImage(img.src, "images"),
    type: img.base64 ? ProductImageTypes.base64 : ProductImageTypes.img,
    previewSrc: supaImage(img.preview_src ?? "", "images_preview"),
    base64: img.base64 ?? "",
  }));
};

const getWeight = (weight: string | null, portionUnit: string | null) => {
  if (weight && weight !== "0.000") {
    return weight;
  }

  if (portionUnit === "KGM") {
    return "1";
  }

  return "0.300";
};

export const convertParhato2Product = (product: SupaProducts): Product => {
  const images = getProductImages(product.images);

  const price = Number(product.price).toFixed(0);

  return {
    id: String(product.id),
    name: product.name,
    price: price,
    onSale: product.on_sale ?? false,
    stockStatus: product.stock_status ?? "",
    description: product.description ?? "",
    attributes: product.attributes?.map((attr) => ({
      ...attr,
      id: String(attr.id),
    })),
    categories: product.categories?.map((category) => ({
      ...category,
      id: String(category.id),
    })),
    stockQuantity: product.stock_quantity ?? 0,
    type: product.type as ProductType,
    purchasable: product.purchasable,
    images,
    soldIndividually: product.sold_individually ?? false,
    groupedProducts: product.grouped_products?.map((id) => String(id)),
    variations: product.variations?.map((id) => String(id)),
    averageRating: product.average_rating ?? "",
    dateCreated: product.date_created ?? "",
    defaultAttributes: product.default_attributes ?? [],
    parentId: String(product.parent_id),
    discountPrice:
      product.discount_price && product.discount_price !== "0" && product.discount_price !== "0.00"
        ? product.discount_price
        : "",
    weight: getWeight(product.weight, product.portion_unit),
    portionUnit: product.portion_unit || "",
    regularPrice: product.regular_price ?? "",
    percentFat: product.percent_fat || "",
    fats: product.fats || "",
    grammage: product.grammage || "",
    additionalFeature: product.additional_feature || "",
    typeOfPackaging: product.type_of_packaging || "",
    storageConditions: product.storage_conditions || "",
    productType: product.product_type || "",
    brand: product.brand || "",
    composition: product.composition || "",
    energyValue: product.energy_value || "",
    carbohydrates: product.carbohydrates || "",
    proteins: product.proteins || "",
    sku: product.sku ?? "",
  };
};

export const convertParhato2Category = (category: SupaCategory): Category => {
  const imageData = category.image?.[0];

  const image = imageData?.src
    ? {
        alt: imageData.alt,
        name: imageData.name,
        id: String(imageData.id),
        src: supaImage(imageData.src, "images_categories"),
        type: category.base64 ? ProductImageTypes.base64 : ProductImageTypes.img,
        base64: category.base64 ?? "",
      }
    : undefined;

  return {
    id: String(category.category_id),
    description: category.description,
    parent: category.parent_category_id ?? "0",
    name: removeHtmlEntities(category.category_name ?? ""),
    image: image,
  };
};

export const convertParhato2Review = (review: productReview): ProductReview => {
  return {
    id: String(review.id),
    review: review.review,
    dateCreated: review.date_created,
    fullName: review.reviewer,
    rating: review.rating,
  };
};

export const convertAddressInputCreateForSupa = (
  customerAddress: AddressInput,
  userId: User["id"],
): TablesInsert<"customer_addresses"> => {
  const location = customerAddress.longitude ? `POINT(${customerAddress.longitude} ${customerAddress.latitude})` : null;

  return {
    customer_id: Number(userId),
    apartment_number: customerAddress.apartmentNumber ?? "",
    city: customerAddress.city ?? "",
    entrance: customerAddress.entrance ?? "",
    floor: customerAddress.floor ?? "",
    intercom: customerAddress.intercom ?? "",
    is_flat: customerAddress.isFlat ?? false,
    location: location,
    state: customerAddress.state ?? "",
    street: customerAddress.street ?? "",
    house: customerAddress.house ?? "",
  };
};

export const convertAddressUpdateForSupa = (customerAddress: Address): Omit<TablesInsert<"customer_addresses">, "customer_id"> => {
  return {
    apartment_number: customerAddress.apartmentNumber ?? "",
    city: customerAddress.city ?? "",
    entrance: customerAddress.entrance ?? "",
    floor: customerAddress.floor ?? "",
    intercom: customerAddress.intercom ?? "",
    is_flat: customerAddress.isFlat ?? false,
    location: `POINT(${customerAddress.longitude} ${customerAddress.latitude})`,
    state: customerAddress.state ?? null,
    street: customerAddress.street ?? null,
    house: customerAddress.house ?? null,
  };
};

export const convertSupaAddress = (customerAddress: Tables<"customer_addresses">): Address => {
  const getCustomerFullAddressPayload = {
    street: customerAddress.street ?? "",
    apartmentNumber: customerAddress.apartment_number ?? "",
    city: customerAddress.city ?? "",
    house: customerAddress.house ?? "",
    isFlat: customerAddress.is_flat,
  };

  const fullAddress = getCustomerFullAddress(getCustomerFullAddressPayload);
  const latitude = typeof customerAddress.latitude === "number" ? String(customerAddress.latitude) : undefined;
  const longitude = typeof customerAddress.longitude === "number" ? String(customerAddress.longitude) : undefined;

  return {
    latitude,
    longitude,
    fullAddress,
    city: customerAddress.city ?? "",
    floor: customerAddress.floor ?? "",
    state: customerAddress.state ?? "",
    house: customerAddress.house ?? "",
    entrance: customerAddress.entrance ?? "",
    intercom: customerAddress.intercom ?? "",
    isFlat: customerAddress.is_flat ?? false,
    street: customerAddress.street ?? "null",
    id: customerAddress.id ? String(customerAddress.id) : "",
    apartmentNumber: customerAddress.apartment_number ?? "",
  };
};

export const convertCustomerUpdateAddressPayload = (
  customerAddress: Address,
): UpdateCustomerAddressMutationVariables["address"] => {
  return {
    apartmentNumber: customerAddress.apartmentNumber!,
    city: customerAddress.city!,
    entrance: customerAddress.entrance!,
    floor: customerAddress.floor!,
    house: customerAddress.house!,
    intercom: customerAddress.intercom || "",
    isFlat: customerAddress.isFlat ?? false,
    state: customerAddress.state!,
    street: customerAddress.street!,
    latitude: customerAddress.latitude,
    longitude: customerAddress.longitude,
  };
};

export const convertSupaDeliveryInterval = (interval: Tables<"delivery_intervals">): DeliveryInterval => {
  return {
    id: String(interval.id),
    text: `${interval.time_start} - ${interval.time_end}`,
  };
};

export const convertSupaReview = (review: Tables<"product_reviews">): ProductReview => {
  return {
    id: String(review.id),
    review: review.review,
    dateCreated: review.created_at,
    fullName: review.reviewer,
    rating: review.rating,
  };
};

export const convertOrderReviewSB = (review: OrderReviewType): Omit<SupaOrderReviews, "id" | "created_at"> => {
  const deliveryQuality = review.deliveryQuality ?? review.deliverySpeed ?? review.driverCourtesy ?? 0;

  return {
    delivery_speed: deliveryQuality,
    driver_courtesy: deliveryQuality,
    delivery_quality: deliveryQuality,

    product_quality: review.productQuality,
    assembly_quality: review.productQuality,

    app_usability: review.appUsability ?? null,

    customer_note: review.customerNote || null,
    order_id: Number(review.orderId),
  };
};

export const convertSBOrderReview = (review: SupaOrderReviews): OrderReviewType => {
  return {
    deliverySpeed: review.delivery_speed,
    driverCourtesy: review.driver_courtesy,
    productQuality: review.product_quality,
    assemblyQuality: review.assembly_quality,
    deliveryQuality: review.delivery_quality,
    appUsability: review.app_usability,
    customerNote: review.customer_note ?? null,
    orderId: String(review.order_id),
  };
};

export const convertSupaCartToCart = (cartFromSupa: RawCartResponse): CartState => {
  return {
    cartTotalValue: cartFromSupa.total,
    cartSubtotalValue: cartFromSupa.total + cartFromSupa.reward_points,
    cartProducts: cartFromSupa.products,
    cartProductsMap: cartFromSupa.products.reduce(
      (acc, product) => {
        acc[product.id] = product;
        return acc;
      },
      {} as CartState["cartProductsMap"],
    ),
    coupons: cartFromSupa.coupons,
    rewardPoints: cartFromSupa.reward_points,
    soldOutProducts: cartFromSupa.sold_out_products,
    unavailableProducts: cartFromSupa.unavailable_products,
    updatedAt: cartFromSupa.updated_at,
  };
};

export const convertCartToSupaCart = (cart: CartState): RawCartResponse => {
  return {
    total: cart.cartSubtotalValue,
    products: cart.cartProducts,
    coupons: cart.coupons,
    reward_points: cart.rewardPoints,
    sold_out_products: cart.soldOutProducts,
    unavailable_products: cart.unavailableProducts,
    updated_at: cart.updatedAt,
  };
};

export const convertPaymentMethod = (method: Parhato2PaymentMethodType): PaymentMethod => ({
  id: method.id,
  type: method.type,
  title: method.title,
  enabled: method.enabled,
});

const convertParhatoSelections = (selections: SelectionsPayloadSection["items"]): SelectionsSection["items"] => {
  return selections.map((item) => item.map((product) => convertParhato2Product(product as any as SupaProducts)));
};

export const convertParhato2Promo = (promo: SupaPromo): Promo => {
  const category = promo.category_id ? convertParhato2Category(promo.category_id) : null;

  const rawImages = Array.isArray(promo.images) ? promo.images : [];
  const images = rawImages.map((image: any) => {
    const imgSrc = image?.src ?? image?.url ?? "";
    const src = imgSrc.startsWith("http") ? imgSrc : supaImage(imgSrc, "promos");
    return {
      alt: image?.alt,
      id: String(image?.id ?? ""),
      src,
      type: (image?.base64 ?? promo?.base64) ? ProductImageTypes.base64 : ProductImageTypes.img,
      base64: image?.base64 ?? promo?.base64 ?? "",
    };
  });

  return {
    images,
    category: category,
    id: String(promo.id),
    title: promo.title ?? "",
    order: promo.order ?? 0,
    productIds: promo.product_ids,
    description: promo.description ?? "",
    link_type: promo.link_type ?? null,
    link_value: promo.link_value ?? null,
  };
};

const convertHomeDataPromo = (homeData: PromoPayloadSection): HomeDataSection => {
  const images = homeData.images?.map((image) => ({
    alt: image.alt,
    src: supaImage(image.src, "promos"),
  }));

  return {
    images: images,
    page: homeData.page,
    meta: homeData.meta,
    id: String(homeData.id),
    tag: homeData.tag ?? "",
    type: homeData.type ?? "",
    order: homeData.order ?? 0,
    title: homeData.title ?? "",
    createdAt: homeData.created_at,
    productIds: homeData.product_ids,
    categoryId: homeData.category_id ?? "",
    items: homeData.items.map((promo) => convertParhato2Promo(promo as any as SupaPromo)),
  };
};

const convertHomeDataProductShelves = (homeData: ProductShelvesPayloadSection): HomeDataSection => {
  const images = homeData.images?.map((image) => ({
    alt: image.alt,
    src: supaImage(image.src, "product_shelves"),
    type: homeData.base64 ? ProductImageTypes.base64 : ProductImageTypes.img,
    base64: homeData.base64 ?? "",
  }));

  return {
    images,
    page: homeData.page,
    meta: homeData.meta,
    id: String(homeData.id),
    tag: homeData.tag ?? "",
    type: homeData.type ?? "",
    order: homeData.order ?? 0,
    title: homeData.title ?? "",
    createdAt: homeData.created_at,
    productIds: homeData.product_ids,
    categoryId: homeData.category_id ?? "",
    items: homeData.items?.map((product) => convertParhato2Product(product as any as SupaProducts)) ?? [],
  };
};

const convertHomeDataSelections = (homeData: SelectionsPayloadSection): SelectionsSection => {
  const items = homeData.items.filter(Boolean);
  return {
    page: homeData.page,
    meta: homeData.meta,
    id: String(homeData.id),
    tag: homeData.tag ?? "",
    type: homeData.type ?? "",
    order: homeData.order ?? 0,
    title: homeData.title ?? "",
    createdAt: homeData.created_at,
    productIds: homeData.product_ids,
    categoryId: homeData.category_id ?? "",
    images: homeData.images,
    items: convertParhatoSelections(items),
  };
};

const convertHomeDataRecipes = (homeData: RecipesPayloadSection): RecipesSection => {
  return {
    page: homeData.page,
    meta: homeData.meta,
    id: String(homeData.id),
    tag: homeData.tag ?? "",
    type: homeData.type ?? "",
    order: homeData.order ?? 0,
    title: homeData.title ?? "",
    createdAt: homeData.created_at,
    productIds: homeData.product_ids,
    categoryId: homeData.category_id ?? "",
    images: homeData.images,
    items: homeData.items.map((recipe) => converterSupaRecipeToRecipe(recipe)),
  };
};

const convertHomeDataCategories = (homeData: CategoriesPayloadSection): CategoriesSection => {
  return {
    page: homeData.page,
    meta: homeData.meta,
    id: String(homeData.id),
    images: homeData.images,
    tag: homeData.tag ?? "",
    type: homeData.type ?? "",
    order: homeData.order ?? 0,
    title: homeData.title ?? "",
    createdAt: homeData.created_at,
    productIds: homeData.product_ids,
    categoryId: homeData.category_id ?? "",
    items: {
      parentCategory: homeData.items?.parentCategory
        ? convertParhato2Category(homeData.items.parentCategory as any as SupaCategory)
        : null,
      subCategories:
        homeData.items?.subCategories?.map((category) => convertParhato2Category(category as any as SupaCategory)) ||
        [],
    },
  };
};

export const convertHomeData = (homeData: any): HomeDataSection => {
  switch (homeData.type) {
    case "promos":
    case "slider":
      return convertHomeDataPromo(homeData as PromoPayloadSection);
    case "product_shelves":
      return convertHomeDataProductShelves(homeData as ProductShelvesPayloadSection);
    case "selections":
      return convertHomeDataSelections(homeData as SelectionsPayloadSection);
    case "recipes":
      return convertHomeDataRecipes(homeData as RecipesPayloadSection);
    case "categories":
      return convertHomeDataCategories(homeData as CategoriesPayloadSection);
    default:
      return homeData as HomeDataSection;
  }
};

export const convertParhato2ProductSubscription = (
  productSubscription: ProductSubscription,
): Parhato2ProductSubscription => {
  return {
    customerId: productSubscription.customer_id,
    productId: productSubscription.product_id,
  };
};

export const convertAdditionalFeature = (additionalFeature: SupaAdditionalFeature): AdditionalFeature => {
  return {
    additionalFeature: additionalFeature.additional_feature,
    count: additionalFeature.count,
  };
};

export const convertYooKassaPayloadForSupa = (
  createYooKassaPaymentInput: CreateYooKassaPaymentInput,
): CreateYooKassaPaymentEF => {
  return {
    amount: {
      value: createYooKassaPaymentInput.amount,
      currency: "RUB",
    },
    confirmation: {
      type: "embedded",
    },
    capture: true,
    description: createYooKassaPaymentInput.description || `Заказ на ${createYooKassaPaymentInput.amount} руб.`,
    metadata: {
      order_id: createYooKassaPaymentInput.orderId,
      profile_id: createYooKassaPaymentInput.profileId,
    },
    merchant_customer_id: createYooKassaPaymentInput.profileId,
  };
};

export const converterSupaRecipeToRecipe = (recipe: SupaRecipe): Recipe => {
  return {
    id: recipe.id,
    description: recipe.description || "",
    image: recipe.image ? supaImage(recipe.image, "recipes") : "",
    title: recipe.title || "",
    productIds: recipe.product_ids,
    ingredients: recipe.ingredients.map((ingredient) => ({
      quantity: ingredient.quantity,
      name: ingredient.name,
      portionUnit: ingredient.portion_unit,
    })),
  };
};

export const convertNotifications = (supaNotification: SupaNotifications): Notification => {
  return {
    body: supaNotification.body,
    createdAt: supaNotification.created_at,
    customerId: supaNotification.customer_id,
    data: supaNotification.data,
    id: supaNotification.id,
    isRead: supaNotification.is_read,
    title: supaNotification.title,
    type: supaNotification.type,
    typeName: supaNotification.type_name,
    url: supaNotification.url,
    urlType: supaNotification.url_type,
  };
};

export const convertOrderTracking = (orderTracking: SupaOrderTracking): OrderTracking => {
  return {
    id: orderTracking.id,
    startLatitude: orderTracking.start_latitude,
    startLongitude: orderTracking.start_longitude,
    endLatitude: orderTracking.end_latitude,
    endLongitude: orderTracking.end_longitude,
    currentLatitude: orderTracking.current_latitude,
    currentLongitude: orderTracking.current_longitude,
    status: orderTracking.status,
    startedAt: orderTracking.started_at,
    endedAt: orderTracking.ended_at,
    customerId: orderTracking.customer_id,
    orderId: orderTracking.order_id ?? "",
  };
};

export const convertToReferralStats = (data: ReferralStatsResponse): ReferralStats => {
  return {
    totalPurchases: data.total_purchases,
    totalPointsEarned: data.total_points_earned,
    invitees: data.invitees,
  };
};

export const convertSbpPaymentToPayments = (data: SupaPayments): Payments => {
  return {
    id: data.id,
    createdAt: data.created_at,
    updatedAt: data.updated_at ?? "",
    amount: data.amount,
    comment: data.comment,
    paymentMethod: data.payment_method,
    status: data.status,
    customerId: data.customer_id,
    orderId: data.order_id ?? 0,
    paymentId: data.payment_id ?? "",
    confirmationUrl: data.confirmation_url ?? "",
  };
};

export const convertOrderChatMessage = (raw: any): OrderChatMessage => ({
  id: Number(raw.id),
  chatId: Number(raw.chat_id),
  senderType: raw.sender_type,
  senderCustomerId: raw.sender_customer_id != null ? Number(raw.sender_customer_id) : null,
  senderAdminUserId: raw.sender_admin_user_id != null ? Number(raw.sender_admin_user_id) : null,
  messageType: raw.message_type,
  text: raw.text,
  createdAt: raw.created_at,
  deletedAt: raw.deleted_at,
  attachments: Array.isArray(raw.attachments) ? raw.attachments.map(convertOrderChatAttachment) : [],
});


export const convertOrderChat = (raw: any): OrderChat => ({
  id: Number(raw.id),
  orderId: Number(raw.order_id),
  customerId: Number(raw.customer_id),
  operatorAdminUserId: raw.operator_admin_user_id != null ? Number(raw.operator_admin_user_id) : null,
  status: raw.status,
  lastMessageAt: raw.last_message_at,
  lastMessagePreview: raw.last_message_preview,
  customerLastReadAt: raw.customer_last_read_at,
  operatorLastReadAt: raw.operator_last_read_at,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
});

export const convertOrderChatAttachment = (raw: any): OrderChatAttachment => ({
  id: Number(raw.id),
  chatId: Number(raw.chat_id),
  messageId: raw.message_id != null ? Number(raw.message_id) : null,
  bucketId: raw.bucket_id,
  storagePath: raw.storage_path,
  fileName: raw.file_name,
  mimeType: raw.mime_type,
  fileSize: Number(raw.file_size),
  status: raw.status,
  createdAt: raw.created_at,
  signedUrl: raw.signed_url ? changeHost(raw.signed_url) : null,
});

export const convertOrderChatMessagesPage = (raw: any): OrderChatMessagesPage => ({
  items: Array.isArray(raw?.items) ? raw.items.map(convertOrderChatMessage) : [],
  nextCursor: raw?.nextCursor ? { createdAt: raw.nextCursor.createdAt, id: Number(raw.nextCursor.id) } : null,
});

export const convertOrderChatUnreadSummaryItem = (raw: any): OrderChatUnreadSummaryItem => ({
  chatId: Number(raw.chatId ?? raw.chat_id ?? raw.id),
  orderId: Number(raw.orderId ?? raw.order_id),
  lastMessageAt: raw.lastMessageAt ?? raw.last_message_at ?? null,
  customerLastReadAt: raw.customerLastReadAt ?? raw.customer_last_read_at ?? null,
  unreadCount: Number(raw.unreadCount ?? raw.unread_count ?? 0),
});

export const convertOrderChatUnreadSummary = (raw: any): OrderChatUnreadSummary => ({
  items: Array.isArray(raw?.items) ? raw.items.map(convertOrderChatUnreadSummaryItem) : [],
});
export const converterParhato2Wishlist = (productWishlist: SupaWishlist): CustomerWishlist => {
  return {
    id: productWishlist.id,
    productIds: productWishlist.product_ids,
  };
};
